import {Component, Input, Output, EventEmitter} from '@angular/core';
import {Distributors} from "../../services/Distributors";
import {User} from "../../services/User";
import {Specialties} from "../../services/Specialties";
import {IUser} from "../../interfaces/IUser";
import {JobTypes} from "../../services/JobTypes";
import {Notify} from "../../services/Notify";
import {Config} from "../../services/Config";
import {Themes} from "../../services/Themes";
import {ITheme} from "../../interfaces/ITheme";
import {ISpecialty} from "../../interfaces/ISpecialty";
import {IDistributor} from "../../interfaces/IDistributor";
import {IJobType} from "../../interfaces/IJobType";

const NAME_REGEX = /^[a-zA-Z\s\-]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEA_REGEX = /^[a-zA-Z]{2}\d{7}$/;
const UPIN_REGEX = /^[a-zA-Z0-9]{6}$/;
const NPI_REGEX = /^\d{10}$/;
const MEMBER_ID_REGEX = /^[a-zA-Z0-9]{2,10}$/;
const NAMES_IDENTICAL_ERROR = "'First Name' and 'Last Name' should be different values";

export const HIDDEN_FIELDS_BY_TYPE: {[type_id: number]: string[]} = {
  2: [
    'job_type_id', 'job_title', 'specialty_id', 'dea', 'upin', 'npi',
    'asps_id', 'aad_id', 'position', 'license_number', 'distributor_id',
    'theme_id', 'contact_note'
  ],
  3: [
    'job_type_id', 'specialty_id', 'dea', 'upin', 'npi',
    'aad_id', 'position', 'license_number', 'theme_id'
  ],
};

@Component({
  selector: 'user-card',
  templateUrl: './user-card.html',
})
export class UserCardCmp {
  @Input() user:IUser = <IUser>{};
  @Input() id:number = 0;
  @Input() editModeAllowed = true;
  @Input() infoModeAllowed = true;
  @Input() fields:string[] = [];
  @Input() infoFields:string[] = [];
  @Input() hideInfoFields:string[] = [];
  @Output() update = new EventEmitter();
  @Output() beforeUpdate = new EventEmitter();
  @Output() cancelSave = new EventEmitter();

  public editMode = false;
  public copy;
  public saving;
  public distributorsMap = {};
  public distributorsList:IDistributor[] = [];
  public specialtiesMap = {};
  public specialtiesList:ISpecialty[] = [];
  public jobTypesMap = {};
  public jobTypesList:IJobType[] = [];
  public themes:Array<ITheme> = [];
  public loading = false;
  public validationErrors:{[field: string]: string} = {};

  constructor(public users:User, public distributors:Distributors, public specialties:Specialties,
    public jobTypes:JobTypes, public themesSrv:Themes, public config:Config) {
    this.distributors.loadToMap(this.distributorsMap);
    this.distributors.loadToList(this.distributorsList);
    this.specialties.loadToMap(this.specialtiesMap);
    this.specialties.loadToList(this.specialtiesList);
    this.jobTypes.loadToMap(this.jobTypesMap);
    this.jobTypes.loadToList(this.jobTypesList);
    this.themesSrv.getThemes().subscribe((t) => {this.themes = t;}, () => {});
  }

  ngOnChanges() {
    if (!this.infoModeAllowed || (this.user && !this.user.id && this.editModeAllowed)) {
      this.editMode = true;
    }
    if (this.user && !this.user.id && !this.user.job_type_id) {
      this.user.job_type_id = 4;
    }
    if ((!this.user || !this.user.id || this.id !== this.user.id) && this.id) {
      this.loading = true;
      this.users.getUserInfo(this.id).then((user) => {
        this.loading = false;
        this.user = <IUser>user;
        if (this.infoModeAllowed) {
          this.editMode = false;
        }
      }, (error) => {
        this.loading = false;
        console.error(error);
      });
    }
  }

  switchEditMode() {
    if (!this.editMode) {
      this.copy = JSON.stringify(this.user);
    } else {
      if (this.copy) {
        this.user = JSON.parse(this.copy);
      }
      this.cancelSave.emit(true);
    }
    this.validationErrors = {};
    this.editMode = !this.editMode;
  }

  formatPhone(phone:string):string {
    if (!phone) return '';
    let digits = phone.replace(/\D/g, '');
    if (digits.length >= 10) {
      return digits.substring(0, 3) + '-' + digits.substring(3, 6) + '-' + digits.substring(6, 10);
    }
    return phone;
  }

  onPhoneInput(event:any) {
    let digits = event.target.value.replace(/\D/g, '').substring(0, 10);
    let v = digits;
    if (digits.length > 6) {
      v = digits.substring(0, 3) + '-' + digits.substring(3, 6) + '-' + digits.substring(6);
    } else if (digits.length > 3) {
      v = digits.substring(0, 3) + '-' + digits.substring(3);
    }
    event.target.value = v;
    this.user.phone = v;
  }

  private validate():boolean {
    this.validationErrors = {};

    if (this.showField('fname')) {
      let v = (this.user.fname || '').trim();
      if (!v) {
        this.validationErrors['fname'] = 'Field is required';
      } else if (v.length > 120 || !NAME_REGEX.test(v)) {
        this.validationErrors['fname'] = 'First Name can contain up to 120 letters, spaces or hyphen';
      }
    }

    if (this.showField('lname')) {
      let v = (this.user.lname || '').trim();
      if (!v) {
        this.validationErrors['lname'] = 'Field is required';
      } else if (v.length > 120 || !NAME_REGEX.test(v)) {
        this.validationErrors['lname'] = 'Last Name can contain up to 120 letters, spaces or hyphen';
      }
    }

    if (this.showField('fname') && this.showField('lname') && !this.validationErrors['fname'] && !this.validationErrors['lname']) {
      let f = (this.user.fname || '').trim().toLowerCase();
      let l = (this.user.lname || '').trim().toLowerCase();
      if (f && f === l) {
        this.validationErrors['lname'] = NAMES_IDENTICAL_ERROR;
      }
    }

    if (this.showField('email') && !this.isEmailDisabled()) {
      let v = (this.user.email || '').trim();
      this.user.email = v;
      if (!v) {
        this.validationErrors['email'] = 'Field is required';
      } else if (!EMAIL_REGEX.test(v)) {
        this.validationErrors['email'] = 'Please enter a valid email address (e.g. name@example.com)';
      }
    }

    if (this.showField('phone')) {
      if (this.user.phone) {
        this.user.phone = this.formatPhone(this.user.phone);
      }
      let v = (this.user.phone || '').trim();
      if (!v) {
        this.validationErrors['phone'] = 'Field is required';
      } else {
        let digits = v.replace(/\D/g, '');
        if (digits.length !== 10) {
          this.validationErrors['phone'] = 'Please enter a phone number in the format 123-456-7890';
        }
      }
    }

    if (this.showField('contact_note') && this.user.contact_note) {
      if (this.user.contact_note.length > 160) {
        this.validationErrors['contact_note'] = 'Contact Note can contain up to 160 characters';
      }
    }

    if (this.showField('job_title') && this.user.job_title) {
      if (this.user.job_title.length > 60) {
        this.validationErrors['job_title'] = 'Job Title should contain up to 60 characters';
      }
    }

    if (this.showField('dea') && this.user.dea && this.user.dea.trim()) {
      if (!DEA_REGEX.test(this.user.dea.trim())) {
        this.validationErrors['dea'] = 'DEA should contain 9 characters. The format should be the following 2 letters, 7 digits. For instance, AP5836727';
      }
    }

    if (this.showField('upin') && this.user.upin && this.user.upin.trim()) {
      if (!UPIN_REGEX.test(this.user.upin.trim())) {
        this.validationErrors['upin'] = 'UPIN should contain 6 symbols. Only letters and digits are allowed.';
      }
    }

    if (this.showField('npi') && this.user.npi && this.user.npi.trim()) {
      if (!NPI_REGEX.test(this.user.npi.trim())) {
        this.validationErrors['npi'] = 'NPI should contain 10 symbols. Only digits are allowed';
      }
    }

    if (this.showField('license_number') && this.user.license_number && this.user.license_number.trim()) {
      let v = this.user.license_number.trim();
      if (v.length > 60 || !NAME_REGEX.test(v)) {
        this.validationErrors['license_number'] = 'License number can contain up to 60 letters, spaces or hyphen';
      }
    }

    let memberIdFields = [
      {field: 'aad_id', condition: this.showField('aad_id')},
      {field: 'asps_id', condition: this.user.theme_id == 2 && this.showField('asps_id')},
      {field: 'cap_id', condition: this.user.theme_id == 5 && this.showField('cap_id')},
      {field: 'mgma_id', condition: this.user.theme_id == 4 && this.showField('mgma_id')},
      {field: 'aoa_id', condition: this.user.theme_id == 9 && this.showField('aoa_id')},
    ];
    memberIdFields.forEach(mf => {
      if (mf.condition) {
        let v = (this.user[mf.field] || '').toString().trim();
        if (v && !MEMBER_ID_REGEX.test(v)) {
          this.validationErrors[mf.field] = 'Member id is not valid. Special symbols not allowed and minimum 2 - maximum 10 symbols allowed';
        }
      }
    });

    return Object.keys(this.validationErrors).length === 0;
  }

  save() {
    if (!this.validate()) {
      let firstError = this.validationErrors[Object.keys(this.validationErrors)[0]];
      let notify = new Notify();
      notify.error(firstError, 'Validation Error');
      return;
    }

    this.beforeUpdate.emit(this.user);
    if (this.user && !this.user.__cancelUpdate) {
      this.saving = true;
      this.users.updateUser(this.user).then(() => {
          this.saving = false;
          this.copy = null;
          if (this.infoModeAllowed) {
            this.editMode = false;
          }
          this.update.emit(this.user);
        },
        (response) => {
          console.error(response);
          this.saving = false;
          let notify = new Notify();
          notify.httpErr(response);
        });
    }
  }

  showField(field:string):boolean {
    if (this.isHiddenForType(field)) {
      return false;
    }
    if (!this.fields || this.fields.length < 1) {
      return true;
    }
    return (this.fields.indexOf(field) > -1);
  }

  infoField(field:string):boolean {
    if (this.isHiddenForType(field)) {
      return false;
    }
    if (this.hideInfoFields && this.hideInfoFields.indexOf(field) > -1) {
      return false;
    }
    if (!this.infoFields || this.infoFields.length < 1) {
      return true;
    }
    return (this.infoFields.indexOf(field) > -1);
  }

  isHiddenForType(field:string):boolean {
    if (!this.user || !this.user.type_id) return false;
    const hidden = HIDDEN_FIELDS_BY_TYPE[this.user.type_id];
    return !!hidden && hidden.indexOf(field) > -1;
  }

  isEmailDisabled():boolean {
    if (!this.user.id) return false;
    if (this.user.type_id == this.config.userTypeRep) return true;
    if (this.user.type_id == this.config.userTypeCM) return true;
    return !this.config.allowContactEmailUpdate;
  }

  toggleEmailSuppress($event?) {
    if (this.user && this.user.id) {
      this.user.is_email_suppressed = !this.user.is_email_suppressed;
      this.save();
    }
    if ($event && $event.stopPropagation) {
      $event.stopPropagation();
    }
    return false;
  }
}
